import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import EditorForTitle from "../tools/EditorForTitle";
import { hzSwal as Swal } from "../../server/ShowAlert";

import {
  getFiltersWithItems,
  createItemInFilter,
  deleteItemInFilter,
  editItemPosition,
  editItem,
  getPresignedUrl,
} from "../../server/Filter";
import {
  getSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
} from "../../server/Supplier";
import { getProductsByStatus } from "../../server/Product";

import { DndContext, closestCenter, DragOverlay } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Search, Trash2, X } from "lucide-react";
import LoadingStatus from "../ui/LoadingStatus";

// The "brand" group lists TCS suppliers (no manual ordering in TCS).
const SUPPLIER_TITLE = "hamazon-brand";
const isSupplierGroup = (title) => title === SUPPLIER_TITLE;

const DEFAULT_FILTERS = {
  kashrut: [{ title: "hamazon-kashrut", items: [] }],
  "sub-brand": [{ title: "hamazon-sub-brand", items: [] }],
  "kashrut-feature": [{ title: "hamazon-kashrut-feature", items: [] }],
};

const mapTenantKindTitle = {
  brand: "hamazon-brand",
  kashrut: "hamazon-kashrut",
  "sub-brand": "hamazon-sub-brand",
  "kashrut-feature": "hamazon-kashrut-feature",
  other: "hamazon-other",
};

function groupByTenantKind(arr) {
  const grouped = arr.reduce((acc, item) => {
    const key = item.tenantKind || "other";
    const title = mapTenantKindTitle[key] || key;
    if (!acc[title]) acc[title] = { title, items: [] };
    acc[title].items.push(item);
    return acc;
  }, {});
  return Object.values(grouped);
}

const filterItemsBySearch = (items, searchTerm) => {
  const query = searchTerm.trim().toLowerCase();
  if (!query) return items;
  return items.filter((item) =>
    String(item.name || "")
      .toLowerCase()
      .includes(query),
  );
};

const sortItemsByName = (items, direction) => {
  const sorted = [...items];
  sorted.sort((a, b) => {
    const comparison = String(a.name || "").localeCompare(
      String(b.name || ""),
      "he",
      { numeric: true, sensitivity: "base" },
    );
    return direction === "asc" ? comparison : -comparison;
  });
  return sorted;
};

const getCategoryDisplayItems = (
  items,
  categoryTitle,
  sortByCategory,
  searchTerm,
  { applySearch = true } = {},
) => {
  let displayItems = items;
  const sortDirection = sortByCategory[categoryTitle];
  if (sortDirection) {
    displayItems = sortItemsByName(displayItems, sortDirection);
  }
  if (applySearch) {
    displayItems = filterItemsBySearch(displayItems, searchTerm);
  }
  return displayItems;
};

const Filters = () => {
  const stationId = useSelector((state) => state.user.stationId);
  const [brand, setBrand] = useState({});
  const [activeItem, setActiveItem] = useState(null);
  const [sortByCategory, setSortByCategory] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [suppliers, kashrutRes, subBrandRes, kashrutFeatureRes] =
        await Promise.all([
          getSuppliers().catch((err) => {
            console.error("Error fetching TCS suppliers:", err);
            return [];
          }),
          getFiltersWithItems("kashrut"),
          getFiltersWithItems("sub-brand"),
          getFiltersWithItems("kashrut-feature").catch((err) => {
            console.error("Error fetching kashrut-feature dict:", err);
            return { data: [] };
          }),
        ]);

      const groupedKashrut = groupByTenantKind(kashrutRes.data);
      const groupedSubBrand = groupByTenantKind(subBrandRes.data);
      const groupedKashrutFeature = groupByTenantKind(
        kashrutFeatureRes?.data || [],
      );

      setBrand({
        brand: [{ title: SUPPLIER_TITLE, items: suppliers }],
        kashrut: groupedKashrut.length
          ? groupedKashrut
          : DEFAULT_FILTERS.kashrut,
        "sub-brand": groupedSubBrand.length
          ? groupedSubBrand
          : DEFAULT_FILTERS["sub-brand"],
        "kashrut-feature": groupedKashrutFeature.length
          ? groupedKashrutFeature
          : DEFAULT_FILTERS["kashrut-feature"],
      });
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const isSupplierLinkedToProducts = async (supplier) => {
    let linkedProducts;
    try {
      linkedProducts = await getProductsByStatus(stationId, {
        brandId: supplier.id,
      });
    } catch (err) {
      // PGW answers 404 when PIM has no products for this brandId.
      if (err?.response?.status === 404) return false;
      console.error("Error checking supplier products:", err);
      Swal.fire("שגיאה", "לא ניתן לבדוק אם הספק משויך למוצרים", "error");
      return true;
    }

    if (linkedProducts?.length) {
      Swal.fire(
        "לא ניתן למחוק את הספק",
        `הספק "${supplier.name}" משויך ל-${linkedProducts.length} מוצרים. יש להעביר אותם לספק אחר לפני המחיקה.`,
        "warning",
      );
      return true;
    }
    return false;
  };

  const deleteItem = async (item, category) => {
    const parameterId = item?.id || item?.Id;
    const title = category?.title || "brand";
    const isSupplier = isSupplierGroup(title);

    if (!parameterId) {
      console.error("Missing parameterId", { item });
      return;
    }

    if (
      isSupplier &&
      (await isSupplierLinkedToProducts({ id: parameterId, name: item.name }))
    ) {
      return;
    }

    const result = await Swal.fire({
      title: "האם אתה בטוח?",
      text: "לא ניתן לשחזר פריט זה!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "מחק",
      cancelButtonText: "בטל",
    });

    if (result.isConfirmed) {
      try {
        if (isSupplier) {
          await deleteSupplier(parameterId);
        } else {
          const kind = title.replace("hamazon-", "");
          await deleteItemInFilter({ id: parameterId }, kind);
        }
        await fetchAll();
        Swal.fire("נמחק בהצלחה!", "", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("שגיאה", "מחיקת הפריט נכשלה", "error");
      }
    }
  };

  const onDragStart = (event) => {
    setActiveItem(event.active.data.current ?? null);
  };

  const onDragEnd = async (event) => {
    setActiveItem(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (active.data.current?.categoryTitle !== over.data.current?.categoryTitle)
      return;

    const { categoryTitle, kindKey } = active.data.current;
    if (isSupplierGroup(categoryTitle)) return;

    const category = brand[kindKey]?.find((cat) => cat.title === categoryTitle);
    if (!category) return;

    // Reorder against the list the user sees; search hides items so drag is disabled then.
    if (searchTerm.trim()) return;

    const displayItems = getCategoryDisplayItems(
      category.items,
      categoryTitle,
      sortByCategory,
      searchTerm,
      { applySearch: false },
    );
    const oldIndex = displayItems.findIndex((item) => item.id === active.id);
    const newIndex = displayItems.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...displayItems];
    const [movedItem] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, movedItem);

    setBrand((prev) => ({
      ...prev,
      [kindKey]: prev[kindKey].map((cat) =>
        cat.title === categoryTitle ? { ...cat, items: reordered } : cat,
      ),
    }));

    setSortByCategory((prev) => {
      const next = { ...prev };
      delete next[categoryTitle];
      return next;
    });

    let position = {};
    if (newIndex === 0) {
      position = { kind: "prepend" };
    } else if (newIndex === reordered.length - 1) {
      position = { kind: "append" };
    } else {
      position = {
        kind: "betweenByRank",
        before: reordered[newIndex - 1].rank,
        after: reordered[newIndex + 1].rank,
      };
    }

    await editItemPosition(categoryTitle, movedItem.id, position);
  };

  const categoryTitlesHebrew = {
    "hamazon-brand": "ספק",
    "hamazon-kashrut": "כשרות",
    "hamazon-sub-brand": "מותג",
    "hamazon-kashrut-feature": "כשרויות נוספות",
    "hamazon-other": "אחר",
  };

  const toggleCategorySort = (categoryTitle) => {
    setSortByCategory((prev) => {
      const current = prev[categoryTitle];
      const direction = current === "asc" ? "desc" : "asc";
      return { ...prev, [categoryTitle]: direction };
    });
  };

  const renderSortIndicator = (categoryTitle) => {
    const direction = sortByCategory[categoryTitle];
    if (!direction) return "";
    return direction === "asc" ? "▲" : "▼";
  };

  if (!brand || Object.keys(brand).length === 0) {
    return (
      <div className="filters admin-page admin-page--fill">
        <LoadingStatus />
      </div>
    );
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="filters admin-page admin-page--fill">
        <header className="admin-page__header">
          <div className="admin-page__header-start">
            <h1 className="title-role">סינון</h1>
          </div>
          <div className="admin-page__header-end">
            <div className="filters-search admin-search-affix">
              <input
                type="search"
                className="admin-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="חפש במסננים"
                aria-label="חיפוש במסננים"
              />
              {searchTerm ? (
                <button
                  type="button"
                  className="admin-search-affix__clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="נקה חיפוש"
                  title="נקה חיפוש"
                >
                  <X aria-hidden size={18} strokeWidth={2} />
                </button>
              ) : (
                <span className="admin-search-affix__icon" aria-hidden>
                  <Search aria-hidden size={18} strokeWidth={2} />
                </span>
              )}
            </div>
          </div>
        </header>
        <div className="flex-container filter-container">
          {Object.entries(brand).map(([kindKey, categories]) => (
            <div key={kindKey} className="category-group">
              {categories.map((category) => {
                const sortDirection = sortByCategory[category.title];
                const displayItems = getCategoryDisplayItems(
                  category.items,
                  category.title,
                  sortByCategory,
                  searchTerm,
                );
                const dragDisabled =
                  Boolean(searchTerm.trim()) || isSupplierGroup(category.title);

                if (searchTerm.trim() && displayItems.length === 0) {
                  return null;
                }

                return (
                  <div key={category.title} className="col-lg-4 filter-column">
                    <div className="filter-column-scroll">
                      <div className="filter-column-toolbar">
                        <button
                          type="button"
                          className={`category-title sort-header-btn${
                            sortDirection ? " sort-header-btn--active" : ""
                          }`}
                          onClick={() => toggleCategorySort(category.title)}
                        >
                          <span>
                            {categoryTitlesHebrew[category.title] ||
                              category.title}
                          </span>
                          <span className="sort-indicator">
                            {renderSortIndicator(category.title)}
                          </span>
                        </button>
                        <div className="filter-column-add">
                          <AddFilterPopup
                            kind={category.title}
                            onAdd={(newItem) => {
                              setBrand((prev) => ({
                                ...prev,
                                [kindKey]: prev[kindKey].map((c) =>
                                  c.title === category.title
                                    ? { ...c, items: [...c.items, newItem] }
                                    : c,
                                ),
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <SortableContext
                        items={displayItems.map((i) => i.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <ul>
                          {displayItems?.length > 0 ? (
                            displayItems.map((item) => (
                              <SortableItem
                                key={item.id}
                                item={item}
                                category={category}
                                kindKey={kindKey}
                                onDelete={deleteItem}
                                dragDisabled={dragDisabled}
                                onItemPatch={(itemId, patch) => {
                                  setBrand((prev) => ({
                                    ...prev,
                                    [kindKey]: prev[kindKey].map((c) =>
                                      c.title === category.title
                                        ? {
                                            ...c,
                                            items: c.items.map((it) =>
                                              it.id === itemId
                                                ? { ...it, ...patch }
                                                : it,
                                            ),
                                          }
                                        : c,
                                    ),
                                  }));
                                }}
                              />
                            ))
                          ) : (
                            <li>
                              {searchTerm.trim()
                                ? "לא נמצאו תוצאות"
                                : "אין פריטים להצגה"}
                            </li>
                          )}
                        </ul>
                      </SortableContext>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* הצגת פריט צף בזמן גרירה */}
      <DragOverlay>
        {activeItem ? (
          <div
            style={{
              background: "var(--hz-surface)",
              border:
                "1px solid var(--hz-cta)" /* TODO: was var(--c-filter-teal) — add to tokens */,
              borderRadius: 8,
              padding: "6px 12px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.13)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              opacity: 0.97,
              cursor: "grabbing",
            }}
          >
            <GripVertical size={18} color="var(--hz-muted)" />
            <span style={{ fontSize: 16 }}>{activeItem.itemName}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default Filters;

const AddFilterPopup = ({ kind, onAdd }) => {
  const handleAddClick = async () => {
    let selectedFile = null;
    let showInProductChecked = false;
    const isKashrutFeature = kind === "hamazon-kashrut-feature";

    const { value: itemName, isConfirmed } = await Swal.fire({
      title: "הוסף פריט חדש",
      input: "text",
      inputLabel: "שם הפריט",
      showCancelButton: true,
      confirmButtonText: "הוסף",
      cancelButtonText: "בטל",
      preConfirm: (value) => {
        if (!value) {
          Swal.showValidationMessage("יש להזין שם פריט!");
          return null;
        }
        return value;
      },
      didOpen: () => {
        const container = Swal.getHtmlContainer();
        if (!container) return;

        if (kind === "hamazon-kashrut") {
          const fileInput = document.createElement("input");
          fileInput.type = "file";
          fileInput.style.display = "block";
          fileInput.style.marginTop = "10px";

          fileInput.onchange = (e) => {
            selectedFile = e.target.files?.[0] ?? null;
          };

          container.appendChild(fileInput);
        }

        if (isKashrutFeature) {
          const label = document.createElement("label");
          label.style.display = "flex";
          label.style.alignItems = "center";
          label.style.gap = "8px";
          label.style.marginTop = "12px";
          label.style.cursor = "pointer";
          label.style.userSelect = "none";

          const checkbox = document.createElement("input");
          checkbox.type = "checkbox";
          checkbox.id = "swal-show-in-product";
          checkbox.onchange = (e) => {
            showInProductChecked = e.target.checked;
          };

          const text = document.createElement("span");
          text.textContent = "הצג במוצר";

          label.appendChild(checkbox);
          label.appendChild(text);
          container.appendChild(label);
        }
      },
    });

    if (!isConfirmed) return;

    if (isSupplierGroup(kind)) {
      try {
        const supplier = await createSupplier(itemName);
        if (onAdd) onAdd(supplier);
        Swal.fire("הספק נוסף בהצלחה!", "", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("שגיאה", "הוספת הספק נכשלה", "error");
      }
      return;
    }

    const payload = { name: itemName };
    if (isKashrutFeature && showInProductChecked) {
      payload.showInProduct = true;
    }

    if (kind === "hamazon-kashrut" && selectedFile) {
      const presignRes = await getPresignedUrl(selectedFile, "kashrut-images");
      console.log("presignRes", presignRes);
      const uploadUrl = presignRes.data.uploadUrl;

      const fullurl = await fetch(uploadUrl, {
        method: "PUT",
        body: selectedFile,
        headers: { "Content-Type": selectedFile.type },
      });
      console.log("fullurl", fullurl);

      const url = fullurl.url;
      const lastPart = url.split("/").pop();
      const fileName = lastPart.split("?")[0];
      payload.img = fileName;
    }

    try {
      const res = await createItemInFilter(kind, payload);
      if (onAdd) onAdd(res.data);
      Swal.fire("נוסף בהצלחה!", "", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("שגיאה", "הוספת הפריט נכשלה", "error");
    }
  };

  return (
    <button type="button" className="btn" onClick={handleAddClick}>
      הוסף פריט
    </button>
  );
};

const SortableItem = ({
  item,
  category,
  kindKey,
  onDelete,
  dragDisabled = false,
  onItemPatch,
}) => {
  const isKashrutFeature = category.title === "hamazon-kashrut-feature";
  const showInProduct = item.showInProduct === true;

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: dragDisabled,
    data: {
      kindKey,
      categoryTitle: category.title,
      itemName: item.name,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    touchAction: "none",
  };

  const handleShowInProductChange = async (e) => {
    e.stopPropagation();
    const checked = e.target.checked;
    const kind = String(category.title || "").replace(/^hamazon-/, "");
    const payload = { showInProduct: checked };

    try {
      await editItem(kind, item.id, payload);
      onItemPatch?.(item.id, { showInProduct: checked });
      Swal.fire("עודכן בהצלחה!", "", "success");
    } catch (err) {
      console.error("Error updating showInProduct:", err);
      Swal.fire("שגיאה", "עדכון ההצגה במוצר נכשל", "error");
    }
  };

  return (
    <li ref={setNodeRef} style={style}>
      <div
        className={`sub-title${isKashrutFeature ? " sub-title--with-show-in-product" : ""}`}
      >
        <div className="sub-title__row">
          <div
            className="drag-handle"
            {...listeners}
            {...attributes}
            style={{
              cursor: dragDisabled
                ? "not-allowed"
                : isDragging
                  ? "grabbing"
                  : "grab",
              display: "flex",
              alignItems: "center",
              padding: "0 4px",
              color: "var(--hz-muted)",
              flexShrink: 0,
              opacity: dragDisabled ? 0.4 : 1,
            }}
            title={
              isSupplierGroup(category.title)
                ? "לא ניתן לשנות סדר ספקים"
                : dragDisabled
                  ? "נקה חיפוש כדי לשנות סדר"
                  : "גרור לשינוי סדר"
            }
          >
            <GripVertical size={20} strokeWidth={2} />
          </div>

          <EditorForTitle
            kind={category.title}
            id={item.id}
            element={item}
            name="name"
            dist="filters"
            onSaved={(patch) => onItemPatch?.(item.id, patch)}
            save={
              isSupplierGroup(category.title)
                ? (name) => updateSupplier(item.id, name)
                : undefined
            }
          />

          <div
            className="delete-item"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item, category);
            }}
          >
            <div className="img">
              <Trash2 aria-hidden size={20} strokeWidth={2} />
            </div>
          </div>
        </div>

        {isKashrutFeature ? (
          <label
            className="show-in-product"
            title="הצג במוצר"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={showInProduct}
              onChange={handleShowInProductChange}
              aria-label="הצג במוצר"
            />
            <span>הצג במוצר</span>
          </label>
        ) : null}
      </div>
    </li>
  );
};
